/**
 * useSkeletonLoading Hook
 * Combines forced delay and fade-in animation for skeleton loading states
 */

import { useState, useEffect, useRef } from 'react';
import { Animated, Easing, Platform } from 'react-native';

interface UseSkeletonLoadingOptions {
  /** Actual loading state from data fetching */
  isLoading: boolean;
  /** Optional: Force delay in milliseconds before showing content (reduced for snappier UX) */
  forceDelayMs?: number;
  /** Animation duration in milliseconds (default: 150, masks layout shift) */
  animationDuration?: number;
  /** Whether content is ready (e.g., data length > 0) */
  isContentReady?: boolean;
}

interface UseSkeletonLoadingReturn {
  /** Combined loading state (actual + forced delay) */
  isLoadingWithDelay: boolean;
  /** Animated opacity value for fade-in */
  contentOpacity: Animated.Value;
}

/**
 * Hook that combines forced delay for testing and smooth fade-in animation
 */
const DEFAULT_DELAY_MS = 150;
const TEST_DELAY_MS = 0;

function isTestEnvironment(): boolean {
  if (process.env.NODE_ENV === 'test') return true;
  if (typeof window !== 'undefined') {
    const host = window.location?.hostname ?? '';
    const port = window.location?.port;
    if (host === '127.0.0.1' || host === 'localhost' || port === '6007') return true;
  }
  return false;
}

export const useSkeletonLoading = ({
  isLoading,
  forceDelayMs,
  animationDuration = 150,
  isContentReady = true,
}: UseSkeletonLoadingOptions): UseSkeletonLoadingReturn => {
  const isTest = isTestEnvironment();
  const resolvedDelayMs = isTest ? TEST_DELAY_MS : (forceDelayMs ?? DEFAULT_DELAY_MS);
  const initialOpacity = (isContentReady && !isLoading) || isLoading ? 1 : 0;
  const contentOpacity = useRef(new Animated.Value(initialOpacity)).current;

  const [forceLoading, setForceLoading] = useState(
    () => !isTest && resolvedDelayMs > 0 && !isContentReady
  );

  // In test env, keep content/skeleton visible (opacity 1) so skeleton shows when loading
  useEffect(() => {
    if (isTest) {
      contentOpacity.setValue(1);
    }
  }, [isTest, contentOpacity, isContentReady, isLoading]);

  useEffect(() => {
    if (isTest) return;
    if (isContentReady) {
      setForceLoading(false);
      return;
    }
    const timer = setTimeout(() => setForceLoading(false), resolvedDelayMs);
    return () => clearTimeout(timer);
  }, [isTest, resolvedDelayMs, isContentReady]);

  const isLoadingWithDelay = isTest ? isLoading || !isContentReady : isLoading || forceLoading;

  useEffect(() => {
    if (isTest) return;
    if (!isLoadingWithDelay && isContentReady) {
      Animated.timing(contentOpacity, {
        toValue: 1,
        duration: animationDuration,
        easing: Easing.out(Easing.cubic),
        // iOS only: the native-driver fade mutates the view on the UI thread,
        // which races Fabric surface teardown during navigation and surfaces as
        // the `RetryableMountingLayerException: Unable to find viewState for tag`
        // fatal on Android. Run the fade on the JS thread on Android (no visual
        // change) so the mutation goes through the normal Fabric commit. Web has
        // no native driver. See project_android16_fabric_viewstate_crash.
        useNativeDriver: Platform.OS === 'ios',
      }).start();
    } else if (isLoadingWithDelay) {
      contentOpacity.setValue(1);
    } else {
      contentOpacity.setValue(0);
    }
  }, [isTest, isLoadingWithDelay, isContentReady, contentOpacity, animationDuration]);

  return {
    isLoadingWithDelay,
    contentOpacity,
  };
};


